import { useEffect } from 'react';
import { Box, Grid, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { Section } from '../../../../../components/ui/Section';
import { RISK_LEVELS, SupplierEsgProfile } from '../../../../../mocks/data/supplierEsgDashboardData';
import { applyRoundAndFormat } from '../../../../../utils';
import useCurrencyCodeConfiguration from '../../../../../hooks/configurations/useCurrencyCodeConfiguration';

export interface SpendAnalysisTabProps {
  profiles: SupplierEsgProfile[];
}

const ESG_BANDS = [
  { label: '80-100', test: (score: number) => score >= 80 },
  { label: '60-79', test: (score: number) => score >= 60 && score < 80 },
  { label: '40-59', test: (score: number) => score >= 40 && score < 60 },
  { label: '0-39', test: (score: number) => score < 40 },
];

const SpendAnalysisTab: React.FC<SpendAnalysisTabProps> = ({ profiles }) => {
  const { t } = useTranslation('esgDashboard');
  const { currencyCode, getCurrencyCodeConfiguration } = useCurrencyCodeConfiguration();

  useEffect(() => {
    getCurrencyCodeConfiguration();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const totalSpend = profiles.reduce((sum, p) => sum + p.annualSpend, 0);
  const localSpend = profiles.filter((p) => p.sourcingType === 'Local').reduce((sum, p) => sum + p.annualSpend, 0);
  const sustainableSpend = profiles.filter((p) => p.sustainabilityCertifications.length > 0).reduce((sum, p) => sum + p.annualSpend, 0);
  const smeSpend = profiles.filter((p) => p.isSme).reduce((sum, p) => sum + p.annualSpend, 0);

  const spendByEsgBand = ESG_BANDS.map((band) => ({
    band: band.label,
    spend: profiles.filter((p) => band.test(p.esgScore)).reduce((sum, p) => sum + p.annualSpend, 0),
  }));

  const spendByRisk = RISK_LEVELS.map((risk) => ({
    risk,
    spend: profiles.filter((p) => p.riskLevel === risk).reduce((sum, p) => sum + p.annualSpend, 0),
  }));

  const spendByCertification = Array.from(new Set(profiles.flatMap((p) => [...p.sustainabilityCertifications, ...p.governanceCertifications])))
    .map((cert) => ({
      certification: cert,
      spend: profiles.filter((p) => p.sustainabilityCertifications.includes(cert) || p.governanceCertifications.includes(cert)).reduce((sum, p) => sum + p.annualSpend, 0),
    }));

  const summaryTiles = [
    { label: t('spend.localSuppliers', 'Spend with local suppliers'), value: localSpend },
    { label: t('spend.sustainableSuppliers', 'Spend with sustainable suppliers'), value: sustainableSpend },
    { label: t('spend.smeSuppliers', 'Spend with SMEs'), value: smeSpend },
    { label: t('spend.totalFiltered', 'Total spend (filtered)'), value: totalSpend },
  ];

  return (
    <Grid container
      spacing={4}>
      {summaryTiles.map((tile) => (
        <Grid item
          xs={12}
          sm={6}
          md={3}
          key={tile.label}>
          <Section style={{ boxShadow: 'none' }}>
            <Box sx={{ p: 3 }}>
              <Typography variant="textXS"
                color="text.secondary">{tile.label}</Typography>
              <Typography variant="textLG"
                fontWeight={600}>{applyRoundAndFormat(tile.value, currencyCode, 0)}</Typography>
            </Box>
          </Section>
        </Grid>
      ))}

      <Grid item
        xs={12}
        md={6}>
        <Section style={{ boxShadow: 'none' }}>
          <Box sx={{ p: 4 }}>
            <Typography variant="textSM"
              fontWeight={600}
              sx={{ mb: 2 }}>{t('spend.byEsgBandTitle', 'Spend by ESG score band')}</Typography>
            <ResponsiveContainer width="100%"
              height={220}>
              <BarChart data={spendByEsgBand}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="band" />
                <YAxis />
                <Tooltip formatter={(value: number) => applyRoundAndFormat(value, currencyCode, 0)} />
                <Bar dataKey="spend"
                  fill="#9c27b0" />
              </BarChart>
            </ResponsiveContainer>
          </Box>
        </Section>
      </Grid>

      <Grid item
        xs={12}
        md={6}>
        <Section style={{ boxShadow: 'none' }}>
          <Box sx={{ p: 4 }}>
            <Typography variant="textSM"
              fontWeight={600}
              sx={{ mb: 2 }}>{t('spend.byRiskTitle', 'Spend by supplier risk level')}</Typography>
            <ResponsiveContainer width="100%"
              height={220}>
              <BarChart data={spendByRisk}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="risk" />
                <YAxis />
                <Tooltip formatter={(value: number) => applyRoundAndFormat(value, currencyCode, 0)} />
                <Bar dataKey="spend"
                  fill="#d32f2f" />
              </BarChart>
            </ResponsiveContainer>
          </Box>
        </Section>
      </Grid>

      <Grid item
        xs={12}>
        <Section style={{ boxShadow: 'none' }}>
          <Box sx={{ p: 4 }}>
            <Typography variant="textSM"
              fontWeight={600}
              sx={{ mb: 2 }}>{t('spend.byCertificationTitle', 'Spend by certification')}</Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t('spend.certification', 'Certification')}</TableCell>
                  <TableCell align="right">{t('spend.spend', 'Spend')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {spendByCertification.map((row) => (
                  <TableRow key={row.certification}>
                    <TableCell>{row.certification}</TableCell>
                    <TableCell align="right">{applyRoundAndFormat(row.spend, currencyCode, 0)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        </Section>
      </Grid>
    </Grid>
  );
};

export default SpendAnalysisTab;
